import api from './api'

export interface TelephonyUserConfig {
  id: string
  userId: string
  provider: string
  phoneNumber: string
  allowOutbound: boolean
}

export interface PhoneContact {
  id: string
  phoneNumber: string
  displayName: string | null
}

// ── Phase 1: global-admin number management ──

export interface AdminTelephonyNumber {
  id: string
  twilioSid: string
  phoneNumber: string
  numberType: 'local' | 'toll_free'
  status: 'active' | 'released'
  needsConfiguration: boolean
  smsCompliance: string
  monthlyPriceUsd: string | null
  purchasedAt: string
  assignment:
    | { kind: 'user'; userId: string; username: string | null; allowOutbound: boolean }
    | { kind: 'channel'; channelId: string }
    | { kind: 'main' }
    | { kind: 'default_sms' }
    | null
  // Human-readable assignment display — ADDITIVE to `assignment` (server
  // returns both on every list; deleted targets keep kind+targetId with
  // null name fields so the UI can fall back to the raw id).
  assignedTo: AssignedToDisplay
  drift: string[]
  liveError: string | null
}

export type AssignedToDisplay =
  | { kind: 'channel'; targetId: string; channelName: string | null; serverId: string | null; serverName: string | null }
  | { kind: 'user'; targetId: string; username: string | null; displayName: string | null; avatarUrl: string | null }
  | null

export interface AvailableNumberResult {
  results: { phoneNumber: string; locality: string | null; region: string | null }[]
  monthlyPriceUsd: string | null
}

export interface ComplianceStatus {
  brand: { status: string | null; brandType: string | null; error: string | null }
  campaign: { status: string; errors: unknown[] } | null
  campaignError: string | null
  tollfree: { status: string; rejectionReason: string | null } | null
  tollfreeError: string | null
}

export type AssignBody =
  // Omitted ≠ false: the server preserves an existing grant on a
  // same-number re-assign; a number change or fresh assign defaults false.
  | { kind: 'user'; targetId: string; allowOutbound?: boolean }
  | { kind: 'channel'; targetId: string }
  | { kind: 'main' }
  | { kind: 'default_sms' }

export interface TollfreeBody {
  businessName?: string
  businessWebsite?: string
  notificationEmail: string
  businessStreetAddress: string
  businessCity: string
  businessStateProvinceRegion: string
  businessPostalCode: string
  businessCountry?: string
  // BusinessType enum: PRIVATE_PROFIT | PUBLIC_PROFIT | SOLE_PROPRIETOR | NON_PROFIT | GOVERNMENT
  // registration fields are REQUIRED for non-sole-proprietor submissions.
  businessType?: string
  businessRegistrationNumber: string
  businessRegistrationAuthority?: string
  businessRegistrationCountry?: string
  contactFirstName: string
  contactLastName: string
  contactEmail: string
  contactPhone: string
  optInImageUrl: string
}

// Intentionally partial projection of the server row — the UI refreshes the list after buy anyway.
export interface BuyNumberResult {
  number: { id: string; phoneNumber: string; needsConfiguration: boolean; configureError: string | null }
}

// Telephony attaches to legacy Channel (where prod voice runs today).
// See docs/superpowers/specs/2026-05-12-osprod-88-twilio-telephony-design.md.
export const telephonyApi = {
  channels: {
    // Channel config get/set/delete clients were removed with the os-spaces
    // ChannelTelephonySection form — number↔channel assignment is admin-only
    // (telephonyApi.admin.assign / serverTelephonyApi.assign).
    getExtension: (channelId: string): Promise<{ extension: string; mainNumber: string | null; directNumber: string | null }> =>
      api.get(`/telephony/channels/${channelId}/extension`).then((r) => r.data),
    inviteByPhone: (channelId: string, phoneNumber: string): Promise<{ callSessionId: string }> =>
      api.post(`/telephony/channels/${channelId}/invite-by-phone`, { phoneNumber }).then((r) => r.data),
  },
  users: {
    getConfig: (userId: string): Promise<TelephonyUserConfig | null> =>
      api.get(`/telephony/users/${userId}/config`).then((r) => r.data),
    setConfig: (
      userId: string,
      body: { provider: string; phoneNumber: string },
    ): Promise<TelephonyUserConfig> =>
      api.put(`/telephony/users/${userId}/config`, body).then((r) => r.data),
    deleteConfig: (userId: string): Promise<void> =>
      api.delete(`/telephony/users/${userId}/config`).then(() => undefined),
  },
  calls: {
    // transcriptSessionId rides the dial/accept responses (not just the
    // dm:call:transcription socket event) — on the dialing client the
    // event fires during the request, before any listener mounts.
    accept: (callId: string): Promise<{ transcriptSessionId: string | null }> =>
      api.post(`/telephony/calls/${callId}/accept`).then((r) => r.data),
    end: (callId: string): Promise<void> =>
      api.post(`/telephony/calls/${callId}/end`).then(() => undefined),
    // Server codes: 400 INVALID_REQUEST | INVALID_PHONE_NUMBER | US_CA_ONLY |
    // NO_USER_NUMBER, 403 TELEPHONY_DISABLED | OUTBOUND_NOT_ALLOWED,
    // 429 RATE_LIMITED, 500 SIP_TRUNK_NOT_CONFIGURED, 502 SIP_DIAL_FAILED.
    dial: (phoneNumber: string, extension?: string): Promise<{ callSessionId: string; dmCallRoom: string; dmChannelId: string; transcriptSessionId: string | null }> =>
      api.post(`/telephony/calls/dial`, { phoneNumber, ...(extension ? { extension } : {}) }).then((r) => r.data),
  },
  sms: {
    send: (dmChannelId: string, body: string): Promise<{ messageId: string }> =>
      api.post(`/telephony/sms/send`, { dmChannelId, body }).then((r) => r.data),
  },
  contacts: {
    listForChannel: (channelId: string): Promise<PhoneContact[]> =>
      api.get(`/telephony/contacts/channel/${channelId}`).then((r) => r.data),
    upsertForChannel: (
      channelId: string,
      body: { phoneNumber: string; displayName?: string | null },
    ): Promise<PhoneContact> =>
      api.put(`/telephony/contacts/channel/${channelId}`, body).then((r) => r.data),
  },
  admin: {
    listNumbers: (): Promise<{ numbers: AdminTelephonyNumber[] }> =>
      api.get('/admin/telephony/numbers').then((r) => r.data),
    searchAvailable: (type: 'local' | 'toll_free', areaCode?: string, contains?: string, inRegion?: string): Promise<AvailableNumberResult> =>
      api.get('/admin/telephony/numbers/available', { params: { type, areaCode, contains, inRegion } }).then((r) => r.data),
    buy: (body: { phoneNumber: string; numberType: 'local' | 'toll_free'; monthlyPriceUsd: string | null }): Promise<BuyNumberResult> =>
      api.post('/admin/telephony/numbers/buy', body).then((r) => r.data),
    repair: (id: string): Promise<void> =>
      api.post(`/admin/telephony/numbers/${id}/repair`).then(() => undefined),
    release: (id: string): Promise<void> =>
      api.post(`/admin/telephony/numbers/${id}/release`).then(() => undefined),
    assign: (id: string, body: AssignBody): Promise<void> =>
      api.post(`/admin/telephony/numbers/${id}/assign`, body).then(() => undefined),
    unassign: (id: string, kind: AssignBody['kind']): Promise<void> =>
      api.delete(`/admin/telephony/numbers/${id}/assign`, { data: { kind } }).then(() => undefined),
    compliance: (): Promise<ComplianceStatus> =>
      api.get('/admin/telephony/compliance').then((r) => r.data),
    ensureMessagingService: (): Promise<{ messagingServiceSid: string }> =>
      api.post('/admin/telephony/compliance/messaging-service').then((r) => r.data),
    createCampaign: (): Promise<{ campaignStatus: string }> =>
      api.post('/admin/telephony/compliance/campaign').then((r) => r.data),
    submitTollfree: (body: TollfreeBody): Promise<{ status: string }> =>
      api.post('/admin/telephony/compliance/tollfree', body).then((r) => r.data),
  },
}

// ── Per-server telephony administration (billing skeleton) ──
// Mounted at /api/servers/:serverId/telephony — server owner or global
// admin. Server error codes arrive in err.response.data.error:
//   502 ORPHANED_PURCHASE | TWILIO_API_ERROR, 404 NO_NUMBERS_AVAILABLE (buy)
//   403 NUMBER_NOT_OWNED, 400 CHANNEL_NOT_IN_SERVER | USER_NOT_IN_SERVER,
//   409 PHONE_NUMBER_IN_USE_* | PHONE_NUMBER_ALREADY_ASSIGNED (assign),
//   409 NUMBER_ASSIGNED (release), 400 NOT_TOLL_FREE (tollfree-verification)
//   404 NOT_FOUND, 403 NUMBER_NOT_OWNED, 400 VALIDATION_ERROR (user-config PATCH)

export interface TelephonyModuleState {
  activatedAt: string | null
  deactivatedAt: string | null
  anchorDay: number | null
}

export type ServerCampaignStatus = 'DRAFT' | 'IN_PROGRESS' | 'APPROVED' | 'FAILED'

// Projection the overview returns (no Twilio sids).
export interface ServerCampaignSummary {
  id: string
  status: ServerCampaignStatus
  useCase: string
  description: string | null
  submittedAt: string | null
  createdAt: string
}

// Full row from GET/POST /campaigns.
export interface ServerCampaign extends ServerCampaignSummary {
  messagingServiceSid: string | null
  twilioCampaignSid: string | null
}

export interface ServerTelephonyOverview {
  module: TelephonyModuleState
  numbers: AdminTelephonyNumber[]
  campaigns: ServerCampaignSummary[]
}

export interface ServerBuyNumberBody {
  // Exact E.164 from a prior searchNumbers call — preferred (search-then-pick).
  phoneNumber?: string
  // First-match filters (legacy blind-buy path).
  areaCode?: string
  contains?: string
  tollFree?: boolean
}

export interface ServerSearchNumbersParams {
  type: 'local' | 'toll_free'
  areaCode?: string
  contains?: string
  inRegion?: string
}

// monthlyPriceCents is the real recurring charge against the server balance
// (billing price list), unlike the admin search's Twilio display price.
export interface ServerAvailableNumbersResult {
  results: { phoneNumber: string; locality: string | null; region: string | null }[]
  monthlyPriceCents: number
}

// Intentionally partial projection of the server row — the UI refreshes the
// overview after buy anyway.
export interface ServerBuyNumberResult {
  number: {
    id: string
    phoneNumber: string
    numberType: 'local' | 'toll_free'
    status: 'active' | 'released'
    needsConfiguration: boolean
    serverId: string | null
    configureError: string | null
  }
}

// Server-scoped assignment only targets channels/users in that server
// (main/default_sms stay global-admin-only).
export interface ServerAssignBody {
  kind: 'channel' | 'user'
  targetId: string
  // Omitted ≠ false: the server preserves an existing grant on a
  // same-number re-assign; a number change or fresh assign defaults false.
  allowOutbound?: boolean
}

export interface TollfreeVerificationResult {
  sid: string | null
  status: string
}

export const serverTelephonyApi = (serverId: string) => ({
  overview: (): Promise<ServerTelephonyOverview> =>
    api.get(`/servers/${serverId}/telephony/overview`).then((r) => r.data),
  setModule: (enabled: boolean): Promise<{ module: TelephonyModuleState }> =>
    api.post(`/servers/${serverId}/telephony/module`, { enabled }).then((r) => r.data),
  searchNumbers: (params: ServerSearchNumbersParams): Promise<ServerAvailableNumbersResult> =>
    api.get(`/servers/${serverId}/telephony/numbers/available`, { params }).then((r) => r.data),
  buyNumber: (body: ServerBuyNumberBody): Promise<ServerBuyNumberResult> =>
    api.post(`/servers/${serverId}/telephony/numbers/buy`, body).then((r) => r.data),
  assign: (numberId: string, body: ServerAssignBody): Promise<void> =>
    api.post(`/servers/${serverId}/telephony/numbers/${numberId}/assign`, body).then(() => undefined),
  release: (numberId: string): Promise<void> =>
    api.post(`/servers/${serverId}/telephony/numbers/${numberId}/release`).then(() => undefined),
  campaigns: (): Promise<{ campaigns: ServerCampaign[] }> =>
    api.get(`/servers/${serverId}/telephony/campaigns`).then((r) => r.data),
  createCampaign: (body: { description?: string }): Promise<{ campaign: ServerCampaign }> =>
    api.post(`/servers/${serverId}/telephony/campaigns`, body).then((r) => r.data),
  submitTollfree: (numberId: string, body: { optInImageUrl?: string }): Promise<TollfreeVerificationResult> =>
    api.post(`/servers/${serverId}/telephony/numbers/${numberId}/tollfree-verification`, body).then((r) => r.data),
  setUserOutbound: (userId: string, allowOutbound: boolean): Promise<{ userId: string; allowOutbound: boolean }> =>
    api.patch(`/servers/${serverId}/telephony/user-config/${userId}`, { allowOutbound }).then((r) => r.data),
})
